import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Warning, ShieldCheck, MagnifyingGlass, ClockCounterClockwise } from "@phosphor-icons/react";

const ACTION_LABELS = {
  "login.success": "Login OK",
  "login.fail": "Login fallito",
  "login.lockout": "Account bloccato",
  "user.approve": "Utente approvato",
  "user.reject": "Utente rifiutato",
  "user.delete": "Utente eliminato",
  "practice.mark_paid": "Pratica pagata",
  "practice.delivered": "Pratica consegnata",
  "entity_fees.upload": "Diritti ente caricati",
  "passkey.register": "Passkey aggiunta",
  "passkey.delete": "Passkey rimossa",
  "2fa.enable": "2FA attivata",
  "2fa.disable": "2FA disattivata",
  "service.create": "Servizio creato",
  "service.delete": "Servizio eliminato",
  "gdpr.export": "Export GDPR",
  "gdpr.delete_account": "Eliminazione account GDPR",
};

const ACTION_TONE = {
  "login.fail": "danger",
  "login.lockout": "danger",
  "user.delete": "danger",
  "service.delete": "danger",
  "gdpr.delete_account": "danger",
  "login.success": "success",
  "user.approve": "success",
  "passkey.register": "success",
  "2fa.enable": "success",
  "practice.mark_paid": "success",
  "practice.delivered": "info",
  "gdpr.export": "info",
};

const toneClasses = {
  danger: "bg-red-50 text-red-800 border-red-200",
  success: "bg-emerald-50 text-emerald-800 border-emerald-200",
  info: "bg-sky-50 text-sky-800 border-sky-200",
  neutral: "bg-black/5 text-black/70 border-black/10",
};

export default function AdminAuditLog({ embedded = false } = {}) {
  const [rows, setRows] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [days, setDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const [rlog, rtim] = await Promise.all([
        api.get("/admin/audit-log", { params: { limit: 500 } }),
        api.get("/admin/audit-log/timeline", { params: { days } }),
      ]);
      setRows(rlog.data || []);
      setTimeline(rtim.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento audit log");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/audit-log/export.csv", {
        params: { days: Math.max(days, 30), action: actionFilter || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `housetag_audit_last${Math.max(days, 30)}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore export CSV");
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFilter && r.action !== actionFilter) return false;
      if (!q) return true;
      return (
        (r.action || "").toLowerCase().includes(q) ||
        (r.actor?.email || "").toLowerCase().includes(q) ||
        (r.actor?.name || "").toLowerCase().includes(q) ||
        (r.ip || "").toLowerCase().includes(q) ||
        (r.target_id || "").toLowerCase().includes(q)
      );
    });
  }, [rows, filter, actionFilter]);

  const maxDayTotal = useMemo(() => {
    if (!timeline?.days) return 1;
    return Math.max(1, ...timeline.days.map((d) => d.total || 0));
  }, [timeline]);

  const actions = useMemo(() => {
    const s = new Set(rows.map((r) => r.action).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-audit-log">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div>
          {!embedded && <div className="label-eyebrow">Sicurezza</div>}
          {!embedded && (
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Registro attività</h1>
          )}
          <p className="text-sm text-muted-foreground mt-1">Tutte le azioni sensibili tracciate: login, approvazioni, pagamenti, GDPR, passkey, 2FA.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Periodo:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 px-2 rounded-lg border border-black/15 bg-white text-sm"
            data-testid="audit-days-select"
          >
            <option value={1}>Oggi</option>
            <option value={3}>3 giorni</option>
            <option value={7}>7 giorni</option>
            <option value={14}>14 giorni</option>
            <option value={30}>30 giorni</option>
          </select>
          <button
            onClick={exportCsv}
            className="h-9 px-3 rounded-lg bg-black text-white text-sm hover:opacity-90"
            data-testid="audit-export-csv-btn"
          >
            Esporta CSV
          </button>
        </div>
      </div>

      {/* Timeline chart */}
      {timeline && (
        <div className="rounded-2xl border border-black/10 bg-white p-6 mb-6" data-testid="audit-timeline">
          <div className="flex items-center gap-2 mb-4">
            <ClockCounterClockwise size={20} weight="duotone" />
            <h2 className="font-display text-lg font-semibold">Andamento ultimi {timeline.range_days} giorni</h2>
          </div>
          <div className="flex items-end gap-2 h-40">
            {timeline.days.map((d) => {
              const success = d.login_success || 0;
              const fail = d.login_fail || 0;
              const others = Math.max(0, (d.total || 0) - success - fail);
              const hSuccess = (success / maxDayTotal) * 100;
              const hFail = (fail / maxDayTotal) * 100;
              const hOthers = (others / maxDayTotal) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`audit-day-${d.date}`}>
                  <div className="flex flex-col-reverse items-stretch w-full h-32 justify-start">
                    <div style={{ height: `${hSuccess}%` }} className="w-full bg-emerald-500 rounded-t-sm min-h-[2px]" title={`Login OK: ${success}`}></div>
                    <div style={{ height: `${hFail}%` }} className="w-full bg-red-500 min-h-[2px]" title={`Login fail: ${fail}`}></div>
                    <div style={{ height: `${hOthers}%` }} className="w-full bg-black/40 rounded-t-sm min-h-[2px]" title={`Altri eventi: ${others}`}></div>
                  </div>
                  <div className="text-[10px] text-black/60 tabular-nums" title={d.date}>
                    {d.date.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500"></span> Login OK</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500"></span> Login falliti</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-black/40"></span> Altri eventi</span>
          </div>

          {/* Top failed IPs */}
          {timeline.top_fail_ips?.length > 0 && (
            <div className="mt-5 border-t border-black/5 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Warning size={16} weight="duotone" className="text-amber-600" />
                <span className="text-sm font-semibold">IP con più tentativi falliti (possibile brute-force)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {timeline.top_fail_ips.map((r) => (
                  <span
                    key={r.ip}
                    className="text-xs px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 tabular-nums"
                    data-testid={`audit-fail-ip-${r.ip}`}
                  >
                    {r.ip} · {r.count}×
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters + table */}
      <div className="rounded-2xl border border-black/10 bg-white p-6">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Cerca email, IP, action..."
              className="w-full pl-9 h-9 rounded-lg border border-black/15 bg-white text-sm"
              data-testid="audit-search-input"
            />
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="h-9 px-2 rounded-lg border border-black/15 bg-white text-sm"
            data-testid="audit-action-filter"
          >
            <option value="">Tutte le azioni</option>
            {actions.map((a) => (
              <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-black/60 border-b border-black/10">
                <th className="py-2 pr-2 font-normal">Data</th>
                <th className="py-2 pr-2 font-normal">Azione</th>
                <th className="py-2 pr-2 font-normal">Autore</th>
                <th className="py-2 pr-2 font-normal">IP</th>
                <th className="py-2 pr-2 font-normal">Target</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="py-6 text-center text-black/50">Caricamento...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-black/50">Nessuna attività trovata.</td></tr>
              )}
              {filtered.map((r) => {
                const tone = ACTION_TONE[r.action] || "neutral";
                return (
                  <tr key={r._id} className="border-b border-black/5 hover:bg-black/5" data-testid={`audit-row-${r._id}`}>
                    <td className="py-2 pr-2 tabular-nums text-black/70">{fmt(r.at)}</td>
                    <td className="py-2 pr-2">
                      <span className={`text-xs px-2 py-1 rounded-md border ${toneClasses[tone]}`}>
                        {ACTION_LABELS[r.action] || r.action}
                      </span>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex flex-col">
                        <span className="font-medium">{r.actor?.name || "—"}</span>
                        <span className="text-xs text-black/50">{r.actor?.email || r.actor_id?.slice(0, 8) || "sistema"}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-2 tabular-nums text-black/60">{r.ip || "—"}</td>
                    <td className="py-2 pr-2 text-black/60 truncate max-w-[220px]" title={r.target_id || ""}>
                      {r.target_type ? `${r.target_type}:${(r.target_id || "").slice(0, 8)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-black/40 mt-3 flex items-center gap-2">
          <ShieldCheck size={14} weight="duotone" /> Ultimi {rows.length} eventi tracciati (max 500).
        </div>
      </div>
    </div>
  );
}
